import { Resolution, RenderEffects, Multiplayer, ShopConstants } from "../../../shared/constants";
import * as PIXI from 'pixi.js'
import { animateSetter, SetterAnimation, wait } from "../../../shared/systems/animationSystem";
import { metersToPixels } from "../../../shared/utils";
import { easeOutCubic, easeInCubic } from "../../../shared/interpolation";
import VignetteFrag from "./shaders/fragment/vignette";
import DefaultVert from "./shaders/vertex/default";

export class VignetteEffect {
	private vignette: PIXI.Mesh;
	private activeAnim: SetterAnimation | null = null;

	private aperatureAnimSpeed = RenderEffects.VignetteApertureMsPerPixel;
	private apertureRadius = 0;
	private bloodLevel = 0;

	constructor(private stage: PIXI.Container) {
		const screenSizedPlane = new PIXI.Geometry()
			.addAttribute('aVertexPosition', [
				0, 0,
				Resolution.Width, 0,
				Resolution.Width, Resolution.Height,
				0, Resolution.Height
			], 2)
			.addAttribute('uv', [
				0, 0,
				1, 0,
				1, 1,
				0, 1
			], 2)
			.addIndex([
				0, 1, 2, 0, 2, 3
			]);
		const vignetteShader = PIXI.Shader.from(DefaultVert, VignetteFrag, {
			aspect: Resolution.Width / Resolution.Height,
			aperture: 0.0,
			color: [0, 0, 0]
		});
		this.vignette = new PIXI.Mesh(screenSizedPlane, vignetteShader);
		this.vignette.position.set(0, 0);
		this.setAperture(this.apertureRadius);
	}

	setAperture = (radius: number) => {
		this.apertureRadius = radius;
		this.vignette.shader.uniforms.aperture = (1.0 - (radius / Resolution.Height));
	}

	getAperture = () => this.apertureRadius;

	setBloodLevel = (level: number) => {
		this.bloodLevel = level;
		this.vignette.shader.uniforms.color = [level, 0, 0];
	}

	async animateAperture(radius: number, durationSec=0.2) {
		if (this.activeAnim != null && this.activeAnim.getProgress() < 1) {
			this.activeAnim.cancel();
		}
		const current = this.apertureRadius;

		if (durationSec === 0) return Promise.resolve();
		this.activeAnim = new SetterAnimation(current, this.setAperture, radius, durationSec * 1000).start();
		return this.activeAnim.done;
	}

	bloodAnim: SetterAnimation | null = null;
	async playBloodVisionEffect(duration: number, isImmuneCell = false) {
		if (this.bloodAnim !== null) {
			this.bloodAnim.cancel();
		}
		const halfDuration = duration;
		this.setBloodLevel(1);
		await wait(halfDuration)
		this.bloodAnim = new SetterAnimation(this.bloodLevel, this.setBloodLevel, 0, halfDuration, easeOutCubic).start();
		return this.bloodAnim.done;
	}

	async apply() {
		this.stage.addChild(this.vignette);
		this.animateAperture(0);
	}

	async remove() {
		const radiusInPixels = metersToPixels(RenderEffects.VignetteApertureFull);
		await this.animateAperture(radiusInPixels);
		this.vignette.parent?.removeChild(this.vignette);
	}
}
